export function getWellSkyDeactivationReason(client) {
  const reason =
    client?.integrations?.wellsky?.deactivationReason ||
    client?.clientDeactivationReason ||
    "";
  return String(reason || "").trim();
}

/** WellSky status fields returned by GET /clients/:id — pass through to clientMeta. */
export function mergeWellSkyStatusFields(client = {}) {
  if (!client || typeof client !== "object") return {};
  return {
    wellSkyId:
      client.wellSkyId || client?.integrations?.wellsky?.externalId || null,
    wellSkyActive: client.wellSkyActive,
    integrations: client.integrations,
    clientDeactivationReason: client.clientDeactivationReason,
    deactivatedDate: client.deactivatedDate,
    status: client.status,
  };
}

export function getWellSkyDeactivatedDate(client) {
  const raw =
    client?.integrations?.wellsky?.deactivatedDate ||
    client?.deactivatedDate ||
    null;
  if (!raw) return "";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** Resolve WellSky active flag for a client document (true/false/null). */
export function resolveWellSkyActive(client) {
  const deactivationReason = getWellSkyDeactivationReason(client);
  const deactivatedDate = getWellSkyDeactivatedDate(client);
  if (deactivationReason || deactivatedDate) return false;

  if (typeof client?.wellSkyActive === "boolean") return client.wellSkyActive;
  if (typeof client?.integrations?.wellsky?.active === "boolean") {
    return client.integrations.wellsky.active;
  }
  const raw = String(client?.status || "").trim().toLowerCase();
  if (raw === "active") return true;
  if (raw === "inactive") return false;
  if (typeof client?.active === "boolean") return client.active;
  if (typeof client?.isActive === "boolean") return client.isActive;

  const wellSkyLinked =
    Boolean(client?.wellSkyId) ||
    Boolean(client?.integrations?.wellsky?.externalId);
  // Legacy WellSky imports only pulled active=true patients (before inactive sync).
  if (wellSkyLinked) return true;

  return null;
}

/** Filter key for FolderView status dropdown: active | inactive | unknown */
export function getClientStatusFilterKey(client) {
  const active = resolveWellSkyActive(client);
  if (active === true) return "active";
  if (active === false) return "inactive";
  return "unknown";
}

/** Display label: "Active" | "DeActive" | null (non-WellSky / unknown) */
export function getClientWellSkyStatusLabel(client) {
  const active = resolveWellSkyActive(client);
  if (active === true) return "Active";
  if (active === false) return "DeActive";
  return null;
}

export function isWellSkyClient(client) {
  return (
    Boolean(client?.wellSkyId) ||
    Boolean(client?.integrations?.wellsky?.externalId) ||
    typeof client?.wellSkyActive === "boolean"
  );
}

/** Count Active / DeActive WellSky clients in a folder list. */
/** Active roster for dashboards / Care Hub — exclude only explicit WellSky deactivations. */
export function isMonitoredCareHubClient(client) {
  const key = getClientStatusFilterKey(client);
  return key === "active" || key === "unknown";
}

export function countWellSkyClientStatuses(clients = []) {
  let active = 0;
  let deactive = 0;
  for (const client of clients) {
    if (!isWellSkyClient(client)) continue;
    const key = getClientStatusFilterKey(client);
    if (key === "active") active += 1;
    else if (key === "inactive") deactive += 1;
  }
  return { active, deactive, total: active + deactive };
}

/** Tooltip for DeActive tags — reason and/or date from WellSky metadata. */
export function getWellSkyDeactivationTooltip(client) {
  if (resolveWellSkyActive(client) !== false) return undefined;
  const reason = getWellSkyDeactivationReason(client);
  const date = getWellSkyDeactivatedDate(client);
  if (reason && date) return `Deactivated: ${reason} (${date})`;
  if (reason) return `Deactivated: ${reason}`;
  if (date) return `Deactivated on ${date}`;
  return "Deactivated in WellSky";
}
